import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { API_CHATBOT_BASE_URL } from '../../constants/config';

const FAQManagement = () => {
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({ question: '', answer: '', category: 'General' });
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [openId, setOpenId] = useState(null);

    useEffect(() => {
        fetchFaqs();
    }, []);

    const fetchFaqs = async () => {
        setLoading(true);
        try {
            const response = await api.get(`${API_CHATBOT_BASE_URL}api/v1/admin/faqs`);
            // Support both array response or paginated response structure
            setFaqs(Array.isArray(response.data) ? response.data : response.data.data || []);
        } catch (err) {
            console.error('Failed to fetch FAQs', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleEdit = (faq) => {
        setFormData({
            question: faq.question,
            answer: faq.answer,
            category: faq.category || 'General'
        });
        setEditId(faq.id);
        setIsEditing(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancel = () => {
        setFormData({ question: '', answer: '', category: 'General' });
        setIsEditing(false);
        setEditId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            if (isEditing) {
                await api.put(`${API_CHATBOT_BASE_URL}api/v1/admin/faqs/${editId}`, formData);
                setSuccess('FAQ updated successfully');
            } else {
                await api.post(`${API_CHATBOT_BASE_URL}api/v1/admin/faqs`, formData);
                setSuccess('FAQ added successfully');
            }

            handleCancel();
            fetchFaqs();

        } catch (err) {
            console.error('Save failed', err);
            const msg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Unknown error';
            setError(`Failed to save FAQ. ${msg}`);
            // Check for auth error
            if (err.response?.status === 401) {
                setError('Authentication failed. Please try logging in again.');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
        try {
            await api.delete(`${API_CHATBOT_BASE_URL}api/v1/admin/faqs/${id}`);
            setFaqs(faqs.filter(f => f.id !== id));
            setSuccess('FAQ deleted');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to delete FAQ');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-6xl mx-auto px-4">
                <div className="mb-8">
                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
                    >
                        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back
                    </button>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">FAQ Management</h1>
                    <p className="text-gray-600">Manage frequently asked questions and sensitive topics for the Chatbot.</p>
                </div>

                {error && <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>}
                {success && <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg">{success}</div>}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Form */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
                            <h2 className="text-xl font-semibold mb-4 text-gray-800">{isEditing ? 'Edit FAQ' : 'Add New FAQ'}</h2>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border rounded-md"
                                    >
                                        <option value="General">General</option>
                                        <option value="Controversial">Controversial / Sensitive</option>
                                        <option value="Policy">Policy</option>
                                        <option value="Candidate">Candidate</option>
                                        <option value="Donation">Donation</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                                    <textarea
                                        name="question"
                                        value={formData.question}
                                        onChange={handleChange}
                                        required
                                        rows={3}
                                        className="w-full px-3 py-2 border rounded-md"
                                        placeholder="e.g. Why should I donate?"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                                    <textarea
                                        name="answer"
                                        value={formData.answer}
                                        onChange={handleChange}
                                        required
                                        rows={6}
                                        className="w-full px-3 py-2 border rounded-md"
                                        placeholder="Detailed answer for the chatbot..."
                                    />
                                </div>
                                <div className="flex gap-2">
                                    {isEditing && (
                                        <button
                                            type="button"
                                            onClick={handleCancel}
                                            className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200"
                                        >
                                            Cancel
                                        </button>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {saving ? 'Saving...' : (isEditing ? 'Update FAQ' : 'Add FAQ')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* List */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-xl font-semibold text-gray-800">Existing FAQs</h2>
                            </div>
                            {loading ? (
                                <div className="p-8 text-center text-gray-500">Loading FAQs...</div>
                            ) : faqs.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">No FAQs found.</div>
                            ) : (
                                <ul className="divide-y divide-gray-200">
                                    {faqs.map(faq => (
                                        <li key={faq.id} className="hover:bg-gray-50 transition-colors">
                                            <div
                                                className="p-6 cursor-pointer"
                                                onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                                            >
                                                <div className="flex justify-between items-start gap-4">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${faq.category === 'Controversial' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                                                                {faq.category}
                                                            </span>
                                                        </div>
                                                        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                                                            <svg
                                                                className={`w-5 h-5 text-gray-400 transform transition-transform ${openId === faq.id ? 'rotate-90' : ''}`}
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                            </svg>
                                                            {faq.question}
                                                        </h3>
                                                    </div>
                                                    <div className="flex gap-3 shrink-0" onClick={e => e.stopPropagation()}>
                                                        <button
                                                            onClick={() => handleEdit(faq)}
                                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium px-2 py-1 rounded hover:bg-blue-50"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(faq.id)}
                                                            className="text-red-500 hover:text-red-700 text-sm font-medium px-2 py-1 rounded hover:bg-red-50"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>

                                                {openId === faq.id && (
                                                    <div className="mt-4 pl-7 text-gray-600 space-y-2 border-t pt-4 animate-fadeIn">
                                                        <p className="whitespace-pre-wrap leading-relaxed">{faq.answer}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FAQManagement;
